import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormControl, Grid, MenuItem, Select } from '@mui/material';

import { BarStack } from '@visx/shape';
import { Group } from '@visx/group';
import { useTooltip, TooltipWithBounds } from '@visx/tooltip';
import { AxisLeft, AxisBottom } from '@visx/axis';
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale';

import {
  getParams,
  thousandFormat,
  thousandFormat2,
} from '../../../../Utils/Functions';
import {
  positiveActiveColor,
  positiveDisableColor,
  activeLabelColor
} from '../../../../Assets/js/constant';

let tooltipTimeout;

const TopChart = ({
  summaryData,
  filterName,
  selectedTopItems,
  handleFilter,
}) => {
  const { tooltipOpen, tooltipLeft, tooltipTop, tooltipData, showTooltip, hideTooltip } = useTooltip();

  const [resize, setResize] = useState(false);
  const [hoverBar, setHoverBar] = useState(null);
  const [selectedBars, setSelectedBars] = useState([]);
  const [data, setData] = useState([]);
  const [maxData, setMaxData] = useState(0);

  const onResize = useCallback(() => {
    setResize(prev => !prev);
  }, []);

  useEffect(() => {
    prepareData();
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(() => {
    prepareData();
  }, [summaryData, filterName]);

  const prepareData = useCallback(() => {
    let newData = [],
      _data = [],
      newMaxData = 0;

    if (filterName !== '' && summaryData) {
      _data['Director'] = [];
      _data['Project Type'] = [];
      _data['Project Source 1'] = [];
      _data['Project Source 2'] = [];

      summaryData.forEach((item) => {
        if (item['Director']) {
          if (!_data['Director'][item['Director']]) {
            _data['Director'][item['Director']] = [];
            _data['Director'][item['Director']]['max'] = 0;
          }
          _data['Director'][item['Director']]['max'] += parseFloat(
            item['AmountYetToInvoice'],
          );
        }

        if (item['Project Type']) {
          if (!_data['Project Type'][item['Project Type']]) {
            _data['Project Type'][item['Project Type']] = [];
            _data['Project Type'][item['Project Type']]['max'] = 0;
          }
          _data['Project Type'][item['Project Type']]['max'] += parseFloat(
            item['AmountYetToInvoice'],
          );
        }

        ['Project Source 1', 'Project Source 2'].forEach((sourceField) => {
          if (item[sourceField]) {
            if (!_data[sourceField][item[sourceField]]) {
              _data[sourceField][item[sourceField]] = [];
              _data[sourceField][item[sourceField]]['max'] = 0;
            }
            _data[sourceField][item[sourceField]]['max'] += parseFloat(
              item['AmountYetToInvoice'],
            );
          }
        });
      });
    }

    if (_data[filterName]) {
      // skip non-selected item.
      var _item,
        item = _data[filterName];
      Object.keys(item).forEach((key) => {
        if (item[key]['max'] > newMaxData) newMaxData = item[key]['max'];

        _item = [];
        _item['name'] = key;
        _item['value'] = item[key]['max'];
        newData.push(_item);
      });
    }

    // data = []; // error test

    setData(newData);
    setMaxData(newMaxData);
  }, [summaryData, filterName]);

  const handleFilterChange = useCallback((event) => {
    const newFilterName = event.target.value;
    handleFilter({ filterName: newFilterName });
    handleFilter({
      selectedTopItems: [],
    });
  }, [handleFilter]);

  const getColor = useCallback((bar) => {
    const name = bar.bar.data.name;

    const activeColor = positiveActiveColor;
    const disableColor = positiveDisableColor;

    if (selectedTopItems.length === 0) {
      return activeColor;
    }

    for (let i = 0; i < selectedTopItems.length; i++) {
      if (selectedTopItems[i] === name) {
        return activeColor;
      }
    }

    return disableColor;
  }, [selectedTopItems]);

  const handleBar = useCallback((event, bar) => {
    let _selectedBars, _selectedTopItems;

    const name = bar.bar.data.name;

    if (event.shiftKey) {
      _selectedBars = selectedBars.slice();
      _selectedTopItems = selectedTopItems.slice();

      let index = NaN;
      for (let i = 0; i < selectedBars.length; i++) {
        if (selectedBars[i] === event.target) {
          index = i;
          break;
        }
      }

      if (isNaN(index)) {
        event.target.classList.add('barActive');
        _selectedBars.push(event.target);
        _selectedTopItems.push(name);
      } else {
        event.target.classList.remove('barActive');
        _selectedBars.splice(index, 1);
        _selectedTopItems.splice(index, 1);
      }
    } else {
      let exist = false;
      selectedBars.forEach((selectedBar) => {
        selectedBar.classList.remove('barActive');
        if (selectedBar === event.target) exist = true;
      });

      if (exist && selectedBars.length === 1) {
        _selectedTopItems = [];
        _selectedBars = [];
      } else {
        _selectedTopItems = [name];
        _selectedBars = [event.target];
        event.target.classList.add('barActive');
      }
    }

    setSelectedBars(_selectedBars);

    handleFilter({
      selectedTopItems: _selectedTopItems,
    });
  }, [selectedBars, selectedTopItems, handleFilter]);

  const handleLabel = useCallback((event, name) => {
    let _selectedTopItems;

    if (event.shiftKey) {
      _selectedTopItems = selectedTopItems.slice();
      const index = selectedTopItems.indexOf(name);
      if (index === -1) {
        _selectedTopItems.push(name);
      } else {
        _selectedTopItems.splice(index, 1);
      }
    } else {
      const index = selectedTopItems.indexOf(name);
      _selectedTopItems = index === -1 ? [name] : [];
    }

    setSelectedBars([]);
    
    handleFilter({
      selectedTopItems: _selectedTopItems,
    });
  }, [selectedTopItems, handleFilter]);

  const deSelectAll = useCallback(() => {
    if (hoverBar) hoverBar.classList.remove('barHover');

    selectedBars.forEach((selectedBar) => {
      selectedBar.classList.remove('barActive');
    });

    setHoverBar(null);
    setSelectedBars([]);
    localStorage.removeItem('wihSelectedTopItems');
    handleFilter({
      selectedTopItems: [],
    });
  }, [hoverBar, selectedBars, handleFilter]);

  const showTooltipHandler = useCallback((event, xScale, bar, isBar = true) => {
    if (tooltipTimeout) clearTimeout(tooltipTimeout);

    let data = {},
      top,
      left;

    top = event.nativeEvent.layerY - 20;
    left =
      window.innerWidth / 2 < event.nativeEvent.layerX
        ? event.nativeEvent.layerX - 170
        : event.nativeEvent.layerX + 50;

    if (isBar) {
      if (hoverBar) hoverBar.classList.remove('barHover');
      event.target.classList.add('barHover');
      setHoverBar(event.target);
      data = bar;
    } else {
      data['forecast'] = bar;
    }

    data['isBar'] = isBar;

    showTooltip({
      tooltipData: data,
      tooltipTop: top,
      tooltipLeft: left,
    });
  }, [hoverBar, showTooltip]);

  const hideTooltipHandler = useCallback(() => {
    tooltipTimeout = setTimeout(() => {
      if (hoverBar) hoverBar.classList.remove('barHover');
      setHoverBar(null);

      hideTooltip();
    }, 300);
  }, [hoverBar, hideTooltip]);

  let xTransform = 0;
  let maxYValue = maxData;
  if (
    !isFinite(maxYValue) ||
    maxYValue === Number.NEGATIVE_INFINITY ||
    isNaN(maxYValue) ||
    maxYValue === 0
  )
    xTransform = 30;
  else {
    if (thousandFormat2(maxYValue).length <= 6) xTransform = 55;
    else {
      if (parseInt(thousandFormat(maxYValue)[0]) > 7)
        xTransform = 7.6 * (thousandFormat2(maxYValue).length + 1) + 5;
      else xTransform = 7.6 * thousandFormat2(maxYValue).length + 5;
    }
  }

  const width = window.innerWidth - 16;
  const height = 150;
  const margin = {
    top: 10,
    right: 0,
    bottom: 20,
    left: xTransform,
  };

  const { pageYOffset, xMax, yMax } = getParams(
    window,
    width,
    height,
    margin,
  );

  const x = (d) => d.name;

  const sortedData = data.sort((a, b) => -a.value + b.value);
  
  // scales
  const xScale = scaleBand()
    .domain(sortedData.map(x))
    .rangeRound([0, xMax])
    .padding(0.2);

  const yScale = scaleLinear({
    domain: [0, maxData],
    range: [yMax, 0],
    nice: true,
  });

  const color = scaleOrdinal({
    domain: ['value'],
    range: [positiveActiveColor],
  });

  return (
    <div>
      <div
        className="wrapper"
        style={{ display: 'flex', alignItems: 'center', height: 33 }}
      >
        <FormControl variant="standard" sx={{
          margin: 7,
          minWidth: 120,
          maxWidth: 300,
          marginLeft: 0,
        }}>
          <Select
            sx={{
              '& .MuiInputBase-input': {
                padding: 0,
                paddingRight: 24,
                fontWeight: 'bold',
                fontFamily: 'Verdana !important',
                fontSize: '10pt',
              },
            }}
            value={filterName}
            name="filterName"
            onChange={handleFilterChange}
          >
            <MenuItem value="Director">Director</MenuItem>
            <MenuItem value="Project Type">Project Type</MenuItem>
            <MenuItem value="Project Source 1">Project Source 1</MenuItem>
            <MenuItem value="Project Source 2">Project Source 2</MenuItem>
          </Select>
        </FormControl>
      </div>
      <Grid container>
        <svg width={width} height={height}>
          <rect
            x={0}
            y={0}
            width={width}
            height={height}
            fill="transparent"
            onClick={deSelectAll}
          />

          <Group top={margin.top} left={margin.left}>
            <BarStack
              data={sortedData}
              keys={['value']}
              x={x}
              xScale={xScale}
              yScale={yScale}
              color={color}
            >
              {(barStacks) => {
                return barStacks.map((barStack) => {
                  return barStack.bars.map((bar) => {
                    return (
                      <rect
                        key={`bar-income-${barStack.index}-${bar.index}`}
                        x={bar.x}
                        y={bar.y}
                        height={isNaN(bar.height) ? 0 : Math.abs(bar.height)}
                        width={bar.width}
                        fill={getColor(bar)}
                        onClick={(event) => handleBar(event, bar)}
                        onMouseLeave={(event) => hideTooltipHandler()}
                        onMouseMove={(event) =>
                          showTooltipHandler(event, xScale, bar)
                        }
                        onTouchEnd={(event) => hideTooltipHandler()}
                        onTouchMove={(event) =>
                          showTooltipHandler(event, xScale, bar)
                        }
                      />
                    );
                  });
                });
              }}
            </BarStack>

            <AxisLeft
              numTicks={4}
              scale={yScale}
              stroke="black"
              tickStroke="black"
              tickLabelProps={(value, index) => ({
                fill: 'black',
                fontSize: 11,
                textAnchor: 'end',
                dy: '0.33em',
                dx: '-0.33em',
              })}
              tickComponent={({ formattedValue, ...tickProps }) => (
                <text {...tickProps} fill="black">
                  ${formattedValue}
                </text>
              )}
            />

            <AxisBottom
              hideTicks={true}
              scale={xScale}
              top={yMax}
              stroke="black"
              tickStroke="black"
              tickLabelProps={(value, index) => ({
                fontSize: 11,
                textAnchor: 'middle',
                dy: '-0.5em',
              })}
              tickComponent={({ formattedValue, ...tickProps }) => (
                <text
                  {...tickProps}
                  fill={
                    selectedTopItems.find(item => item === formattedValue)
                      ? activeLabelColor
                      : 'black'
                  }
                  onClick={(event) =>
                    handleLabel(event, formattedValue)
                  }
                >
                  {formattedValue}
                </text>
              )}
            />
          </Group>
        </svg>

        {tooltipOpen && (
          <TooltipWithBounds
           
            top={tooltipTop + pageYOffset}
            left={tooltipLeft}
          >
            {tooltipData.isBar ? (
              <div>
                <div className="pdv-10">
                  <strong>{tooltipData.bar.data.name} </strong>
                </div>
                <div className="ft-12">
                  WIH:{' '}
                  <strong>
                    ${thousandFormat(tooltipData.bar.data.value)}
                  </strong>
                </div>
              </div>
            ) : (
              <div>
                Sum IncomeMTDForecast HistoricalOnly ={' '}
                {thousandFormat(tooltipData.forecast)}
              </div>
            )}
          </TooltipWithBounds>
        )}
      </Grid>
    </div>
  );
};

TopChart.propTypes = {
  summaryData: PropTypes.array.isRequired,
  filterName: PropTypes.string.isRequired,
  selectedTopItems: PropTypes.array.isRequired,
  handleFilter: PropTypes.func.isRequired,
};

export default TopChart;
